import { useState, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { exportToCSV, exportToExcel, exportToPDF, type ExportColumn } from '../lib/export.ts';
import { Icon } from './Icon.tsx';

interface Props<T extends Record<string, unknown>> {
  rows: T[];
  columns: ExportColumn[];
  filename: string;
  title: string;
  subtitle?: string;
  disabled?: boolean;
}

export function ExportMenu<T extends Record<string, unknown>>({
  rows,
  columns,
  filename,
  title,
  subtitle,
  disabled,
}: Props<T>) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const [pos, setPos] = useState({ top: 0, right: 0 });

  useLayoutEffect(() => {
    if (open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + 4, right: window.innerWidth - r.right });
    }
  }, [open]);

  async function run(kind: 'csv' | 'xlsx' | 'pdf') {
    setBusy(kind);
    try {
      if (kind === 'csv') await exportToCSV(rows, columns, filename);
      if (kind === 'xlsx') await exportToExcel(rows, columns, filename, title);
      if (kind === 'pdf') await exportToPDF(rows, columns, filename, { title, subtitle });
    } finally {
      setBusy(null);
      setOpen(false);
    }
  }

  return (
    <>
      <button
        ref={btnRef}
        onClick={() => setOpen((o) => !o)}
        disabled={disabled || rows.length === 0}
        className="btn-secondary disabled:opacity-40"
      >
        <Icon name="download" size={14} />
        Export
        <Icon name="chevronDown" size={12} className="opacity-50" />
      </button>
      {open &&
        createPortal(
          <>
            <div
              className="fixed inset-0 z-[100]"
              onClick={() => setOpen(false)}
            />
            <div
              className="fixed z-[101] w-48 bg-white dark:bg-slate-900 rounded-lg shadow-elevated border border-slate-200 dark:border-slate-700 py-1 animate-slide-up"
              style={{ top: pos.top, right: pos.right }}
              onClick={(e) => e.stopPropagation()}
            >
              <MenuItem busy={busy === 'csv'} onSelect={() => run('csv')}>
                CSV (Excel-kompatibel)
              </MenuItem>
              <MenuItem busy={busy === 'xlsx'} onSelect={() => run('xlsx')}>
                Excel (.xlsx)
              </MenuItem>
              <MenuItem busy={busy === 'pdf'} onSelect={() => run('pdf')}>
                PDF (Querformat)
              </MenuItem>
            </div>
          </>,
          document.body,
        )}
    </>
  );
}

function MenuItem({
  busy,
  onSelect,
  children,
}: {
  busy: boolean;
  onSelect: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onSelect}
      disabled={busy}
      className="w-full flex items-center justify-between px-3 py-1.5 text-sm text-left hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors disabled:opacity-50"
    >
      {children}
      {busy && <Icon name="refresh" size={12} className="animate-spin text-brand-500" />}
    </button>
  );
}
